// When a section has contentOriginId = Catalog, its subSections are empty
// in raw state - children are resolved at display time from the template.
// Before any mutation (move, delete, insert...), we must "materialize" the
// path: replace Catalog sections with their template content so that
// getDeepSection can traverse the tree.

import { cloneDeep } from 'lodash';
import type { Writable, WritableDeep } from 'type-fest';

import { EMPTY_ARRAY } from '@e-socna/empty';
import { invariant, isDefined } from '@e-socna/utils';

import { generateId } from '../../../utils/generate-id.util';
import type { MappedReferencesContext } from '../../../contexts/ReferencesContext';

import type { DocumentEditorState } from '../types/editor-state.type';
import type { DocumentEditorCmp_RecursiveSection } from '../fragments/recursive-section.fragment';
import type { DocumentEditorCmp_CustomLayout } from '../fragments/custom-layout.fragment';
import type { DocumentEditorCmp_TemplateDependency } from '../queries/one-template-dependency.query';
import type { Editable } from '../types/editable.type';
import type { Getters } from '../types/props.type';

import { customLayoutsPriority } from './custom-layouts-priority.util';
import { getVirtualRoot } from './get-virtual-root.util';

interface MaterializeSectionContext {
  readonly templateDependencies: readonly DocumentEditorCmp_TemplateDependency[];
  readonly contentOriginsCodeMap: MappedReferencesContext['contentOriginsCodeMap'];
}

interface MaterializeInsertionPointsParams<E extends Editable> {
  readonly state: DocumentEditorState<E>;
  readonly insertionPointRealPaths: readonly (readonly number[])[];
  readonly templateDependencies: readonly DocumentEditorCmp_TemplateDependency[];
  readonly contentOriginsCodeMap: MappedReferencesContext['contentOriginsCodeMap'];
  readonly getEditable: Getters<E>['editable'];
}

const buildSectionCustomLayout = (
  section: Readonly<DocumentEditorCmp_RecursiveSection>,
): Readonly<DocumentEditorCmp_CustomLayout> => ({
  __typename: 'CustomLayout' as const,
  _id: generateId(),
  sectionId: section._id,
  hasPagebreakAfter: section.hasPagebreakAfter,
  hasPagebreakBefore: section.hasPagebreakBefore,
  orientation: section.orientation,
  paperFormat: section.paperFormat,
});

const resolveCustomLayouts = (
  section: Readonly<DocumentEditorCmp_RecursiveSection>,
  customLayoutsAccumulator: readonly DocumentEditorCmp_CustomLayout[],
  templateCustomLayouts: readonly DocumentEditorCmp_CustomLayout[],
) => {
  const merged = customLayoutsPriority(
    customLayoutsAccumulator,
    templateCustomLayouts,
  );

  const match = merged.find((cl) => cl.sectionId === section._id);

  return { merged, match };
};

const materializeCatalogSection = <
  S extends Readonly<DocumentEditorCmp_RecursiveSection>,
>(
  section: S,
  context: Readonly<MaterializeSectionContext>,
  customLayoutsAccumulator: readonly DocumentEditorCmp_CustomLayout[],
): S => {
  const newSection = cloneDeep(
    section,
  ) as Writable<DocumentEditorCmp_RecursiveSection>;

  const { merged: firstPassLayouts } = resolveCustomLayouts(
    newSection,
    customLayoutsAccumulator,
    newSection.customLayouts ?? EMPTY_ARRAY,
  );

  const template = context.templateDependencies.find(
    (tmplt) => tmplt._id === newSection.template?._id,
  );

  if (
    newSection.contentOriginId === context.contentOriginsCodeMap.Catalog._id &&
    template
  ) {
    const templateVirtualRoot = getVirtualRoot(template);

    const { merged: secondPassLayouts, match: sectionLayout } =
      resolveCustomLayouts(
        newSection,
        firstPassLayouts,
        templateVirtualRoot.customLayouts ?? EMPTY_ARRAY,
      );

    newSection.contentOriginId = context.contentOriginsCodeMap.Modified._id;
    newSection.title = templateVirtualRoot.title;
    newSection.content = templateVirtualRoot.content;

    newSection.hasPagebreakAfter =
      sectionLayout?.hasPagebreakAfter ?? templateVirtualRoot.hasPagebreakAfter;

    newSection.hasPagebreakBefore =
      sectionLayout?.hasPagebreakBefore ??
      templateVirtualRoot.hasPagebreakBefore;

    newSection.orientation =
      sectionLayout?.orientation ?? templateVirtualRoot.orientation;

    newSection.paperFormat =
      sectionLayout?.paperFormat ?? templateVirtualRoot.paperFormat;

    newSection.subSections = templateVirtualRoot.subSections.map((subSection) =>
      materializeCatalogSection(subSection, context, secondPassLayouts),
    );

    if (!newSection.isInsertionPoint) {
      newSection._id = generateId();
    }
  } else {
    newSection.contentOriginId = context.contentOriginsCodeMap.Modified._id;

    const { match: sectionLayout } = resolveCustomLayouts(
      newSection,
      firstPassLayouts,
      EMPTY_ARRAY,
    );

    if (isDefined(sectionLayout?.hasPagebreakAfter)) {
      newSection.hasPagebreakAfter = sectionLayout.hasPagebreakAfter;
    }

    if (isDefined(sectionLayout?.hasPagebreakBefore)) {
      newSection.hasPagebreakBefore = sectionLayout.hasPagebreakBefore;
    }

    if (isDefined(sectionLayout?.orientation)) {
      newSection.orientation = sectionLayout.orientation;
    }

    if (isDefined(sectionLayout?.paperFormat)) {
      newSection.paperFormat = sectionLayout.paperFormat;
    }

    newSection.subSections = newSection.subSections.map((subSection) =>
      materializeCatalogSection(
        subSection as DocumentEditorCmp_RecursiveSection,
        context,
        firstPassLayouts,
      ),
    );
  }

  newSection.customLayouts = EMPTY_ARRAY;

  return newSection as typeof section;
};

export const materializeInsertionPoints = <E extends Editable>({
  state,
  insertionPointRealPaths,
  templateDependencies,
  contentOriginsCodeMap,
  getEditable,
}: Readonly<MaterializeInsertionPointsParams<E>>): WritableDeep<E> => {
  const clonedEditable = cloneDeep(state.editable) as WritableDeep<E>;
  const { virtualRoot } = getEditable<true>(clonedEditable);

  const context: MaterializeSectionContext = {
    templateDependencies,
    contentOriginsCodeMap,
  };

  const materializeAncestors = (
    current: DocumentEditorCmp_RecursiveSection,
    remainingPath: readonly number[],
  ): void => {
    if (current.contentOriginId === contentOriginsCodeMap.Catalog._id) {
      Object.assign(
        current,
        materializeCatalogSection(current, context, [
          ...(current.customLayouts ?? EMPTY_ARRAY),
          buildSectionCustomLayout(current),
        ]),
      );
    }

    if (!remainingPath.length) {
      return;
    }

    const [nextLevel, ...rest] = remainingPath;

    invariant(!!nextLevel, 'Next level in path should be defined');

    materializeAncestors(
      current.subSections[nextLevel] as DocumentEditorCmp_RecursiveSection,
      rest,
    );
  };

  insertionPointRealPaths.forEach((insertionPointRealPath) => {
    materializeAncestors(virtualRoot, insertionPointRealPath);
  });

  return clonedEditable;
};
